import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import React, { useState } from "react";
import useSWR from "swr";
import {
  orderUrls,
  StaticPageApiUrls,
  UserApiUrls,
} from "../../../../Api/Auth/ApiUrls";
import {
  customFetcher,
  fetcher,
  sendPostRequestAuth,
} from "../../../../Api/swrConfig";
import useSWRMutation from "swr/mutation";
import { toast } from "react-toastify";
import Button from "../../../../Components/Common/Button";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../../../Context/AuthContext";
import { Loader } from "../../../../Components/Common/Loader";
import Breadcrumb from "../../../../Components/Common/Breadcrumb";
import Tabs from "../../../../Components/Common/Tabs";
import { studentPaidModelTestTabsData } from "../../../../data/SidebarData";
import Modal from "../../../../Components/Common/Modal";
import { checkStudentRouteVisibility } from "../../../../utils/findStudentRouteBySlug";
import { studentUrls } from "../../../../Api/Auth/AppUrls";
import NotFound from "../../../Notfound";

const stripePromise = loadStripe(process.env.REACT_APP_Publishable_Key);
const cardElementOptions = {
  style: {
    base: {
      fontSize: "16px",
      color: "#333",
      "::placeholder": { color: "#aab7c4" },
    },
    invalid: { color: "#fa755a" },
  },
};

const PaymentForm = ({ clientSecret, onClose, mutate }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [saveCard, setSaveCard] = useState(false);

  const { data } = useSWR(UserApiUrls.savedCards, fetcher);
  const savedCards = data?.cards || [];

  const { trigger } = useSWRMutation(orderUrls.payment, sendPostRequestAuth, {
    onSuccess: (data) => {
      mutate();
      toast.info(`You can access your tests from your student dashboard too`);
    },
    onError: (error) => {
      toast.error(error.message || "Payment failed. Please try again.");
    },
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);

    // Get the card number element explicitly
    try {
      let paymentMethodId;

      if (selectedCard) {
        paymentMethodId = selectedCard; // Use selected saved card
      } else {
        const cardElement = elements.getElement(CardNumberElement);
        if (!cardElement) {
          toast.error("Card details not found. Please try again.");
          setIsProcessing(false);
          return;
        }

        // Create a payment method using the card details
        const { paymentMethod, error: pmError } =
          await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
          });

        if (pmError) {
          toast.error(pmError.message || "Failed to create payment method.");
          setIsProcessing(false);
          return;
        }
        paymentMethodId = paymentMethod.id;
      }
      // Confirm payment
      const { paymentIntent, error: confirmError } =
        await stripe.confirmCardPayment(clientSecret, {
          payment_method: paymentMethodId,
          setup_future_usage: "off_session",
        });

      if (confirmError) {
        toast.error(
          confirmError.message || "Payment failed. Please try again."
        );
      } else if (paymentIntent?.status === "succeeded") {
        toast.success("Payment successful!");

        // Send request to backend to update order status
        await trigger({
          paymentIntentId: paymentIntent.id,
          itemName: "Paid Model Test(Mechanical)",
          saveCard: saveCard,
        });

        onClose();
      }
    } catch (err) {
      console.error("Payment error:", err);
      toast.error("An unexpected error occurred.");
    }

    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 rounded-lg p-3">
      {savedCards.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Saved Cards
          </label>
          <select
            className="p-2 border rounded-md w-full"
            value={selectedCard || ""}
            onChange={(e) => setSelectedCard(e.target.value || null)}
          >
            <option value="">Use a new card</option>
            {savedCards.map((card) => (
              <option key={card.id} value={card.id}>
                {`**** **** **** ${
                  card.card.last4
                } (${card.card.brand.toUpperCase()})`}
              </option>
            ))}
          </select>
        </div>
      )}

      {!selectedCard && (
        <>
          <label className="block text-sm font-medium text-gray-700">
            Card Number
          </label>
          <CardNumberElement
            className="p-2 border rounded-md"
            options={cardElementOptions}
          />

          <div className="flex space-x-4">
            <div className="w-1/2">
              <label className="block text-sm font-medium text-gray-700">
                Expiry Date
              </label>
              <CardExpiryElement
                className="p-2 border rounded-md w-full"
                options={cardElementOptions}
              />
            </div>

            <div className="w-1/2">
              <label className="block text-sm font-medium text-gray-700">
                CVC
              </label>
              <CardCvcElement
                className="p-2 border rounded-md w-full"
                options={cardElementOptions}
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="saveCard"
              checked={saveCard}
              onChange={() => setSaveCard(!saveCard)}
              className="h-4 w-4"
            />
            <label htmlFor="saveCard" className="text-sm text-gray-700">
              Save this card for future payments
            </label>
          </div>
        </>
      )}

      <Button
        type="primary"
        className="mt-4 w-full"
        disabled={isProcessing}
        isLoading={isProcessing}
      >
        {isProcessing ? "Processing..." : "Pay Now"}
      </Button>
    </form>
  );
};

const StudentPaidModelTestMechanical = () => {
  const location = useLocation();
  const { token, userData, categoryData } = useAuth();
  const parts = location.pathname.split("/").filter(Boolean);
  const dynamicPath = `/${parts[0]}/${parts[1]}-${parts[2]}`;
  const dynamicSlug = dynamicPath.split("/")[dynamicPath.split("/").length - 1];
  const isVisible = checkStudentRouteVisibility(
    categoryData,
    studentUrls.studentPaidModelTestMechanical
  );

  const [clientSecret, setClientSecret] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data, isLoading, mutate } = useSWR(
    dynamicSlug ? StaticPageApiUrls.info(dynamicSlug) : null,
    (url) => customFetcher(url, token)
  );

  const onBuy = async (item) => {
    const payload = {
      userId: userData?._id,
      testId: item._id,
      customer: {
        id: userData?._id,
        name: userData?.name,
        email: userData?.email,
      },
    };
    await purchaseItem(payload);
  };

  const { trigger: purchaseItem, isMutating } = useSWRMutation(
    orderUrls.create,
    sendPostRequestAuth,
    {
      onSuccess: (data) => {
        toast.success("Processing payment...");
        setClientSecret(data.clientSecret);
        setIsModalOpen(true);
      },
      onError: (error) => {
        toast.error(error.message || "Payment failed. Please try again.");
      },
    }
  );
  const handleDownload = (url) => {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.target = "_blank"; // Opens in a new tab
    anchor.rel = "noopener noreferrer";
    anchor.download = url.split("/").pop() || "download"; // Extract filename
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader />
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb actionButtons={[]} />
      <Tabs tabs={studentPaidModelTestTabsData} />
      {!isVisible ? (
        <NotFound />
      ) : (
        <>
          <div className="flex justify-center items-center">
            {userData ? (
              <div className="container mx-auto grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-6 p-6">
                {data?.data?.tests?.length
                  ? data.data?.tests
                      ?.filter((item) => item.link)
                      .map((item) => (
                        <div
                          key={item._id}
                          className="bg-white shadow-lg hover:shadow-xl rounded-lg p-4 transition-all duration-300"
                        >
                          <button className="block w-full flex justify-center">
                            {!item?.link ? (
                              <img
                                src="/fileDownload.png"
                                alt={item.title}
                                className="w-36 h-36 object-cover transform group-hover:scale-110 transition-transform duration-300"
                              />
                            ) : item?.link?.endsWith(".pdf") ? (
                              <img
                                src="https://storage.googleapis.com/road-to-fe/uploads/1741110111744-5fb0db0b-ee21-4a9e-82f4-f09d87b3bbc6-pdfIcon.png"
                                alt={item.title}
                                className="w-36 h-36 object-cover transform group-hover:scale-110 transition-transform duration-300"
                              />
                            ) : (
                              <img
                                src={item.link}
                                alt={item.title}
                                className="w-36 h-36 object-cover rounded-md"
                              />
                            )}
                          </button>

                          <div className="flex flex-col items-center mt-3 gap-1">
                            <h2 className="text-sm font-semibold">
                              {item.title}
                            </h2>
                            <p className="text-sm font-semibold text-primary">
                              ${item.price}
                            </p>
                            {!item.link ? (
                              <Button
                                onClick={() => onBuy(item)}
                                disabled={isMutating}
                              >
                                {item?.actionButton}
                              </Button>
                            ) : (
                              <Button
                                onClick={() =>
                                  handleDownload(item.link, item.title)
                                }
                                disabled={isMutating}
                              >
                                Download
                              </Button>
                            )}
                          </div>
                        </div>
                      ))
                  : null}
              </div>
            ) : (
              <Link to="/login" className="mt-6">
                <Button type="primary" className="w-full">
                  Login
                </Button>
              </Link>
            )}
          </div>
          <Modal
            isOpen={isModalOpen}
            title={"Payment"}
            onCancel={() => {
              setIsModalOpen(false);
            }}
            isLoading={isMutating}
            formWidth="max-w-[50%]"
            footer={false}
          >
            {clientSecret && (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <PaymentForm
                  onClose={() => setIsModalOpen(false)}
                  clientSecret={clientSecret}
                  mutate={mutate}
                />
              </Elements>
            )}
          </Modal>
        </>
      )}
    </div>
  );
};

export default StudentPaidModelTestMechanical;
